import type { ReactNode } from 'react'
import { View } from 'react-native'
import Icon, { type IconName } from '~/components/Icon'
import { Detail } from '~/components/typography/Content.tsx'
import { cn } from '~/lib/utils'

export type PropertyStat = {
  key: string
  icon: IconName
  value: ReactNode
  textClassName?: string
}

export type PropertyStatsVariant = 'card' | 'row'

type PropertyStatsProps = {
  stats: PropertyStat[]
  variant?: PropertyStatsVariant
  className?: string
  textClassName?: string
  iconSize?: number
  itemClassName?: string
  children?: React.ReactNode
}

function splitIntoColumns<T>(items: T[], columnCount: number): T[][] {
  const columns: T[][] = Array.from({ length: columnCount }, () => [])

  for (let index = 0; index < items.length; index++) {
    columns[index % columnCount].push(items[index])
  }

  return columns
}

export function PropertyStats({
  stats,
  variant = 'row',
  className,
  textClassName,
  iconSize = 14,
  itemClassName,
  children,
}: PropertyStatsProps) {
  const filteredStats = stats.filter((stat) => stat.value)

  const renderStat = (stat: PropertyStat) => (
    <View key={stat.key} className={cn('flex-row gap-2', itemClassName)}>
      <Icon name={stat.icon} size={iconSize} className={cn('text-foreground', textClassName)} />

      <Detail
        numberOfLines={2}
        ellipsizeMode="tail"
        className={cn(
          'text-pretty text-sm/snug text-foreground',
          textClassName,
          stat.textClassName
        )}>
        {stat.value}
      </Detail>
    </View>
  )

  if (variant === 'row') {
    const columns = splitIntoColumns(filteredStats, 2).filter((column) => column.length > 0)

    return (
      <View className={cn('flex-row items-start gap-x-4', className)}>
        {columns.map((columnStats, columnIndex) => (
          <View key={columnIndex} className="flex-1 gap-2.5">
            {columnStats.map(renderStat)}
          </View>
        ))}

        {children}
      </View>
    )
  }

  return (
    <View
      className={cn('flex-row flex-wrap items-center justify-start gap-x-6 gap-y-2', className)}>
      {filteredStats.map(renderStat)}

      {children}
    </View>
  )
}
